// ClientWorkbenchTable — preview-only client-relevance workbench.
//
// Column → payload mapping (`card` = item from `/api/clients/{id}` →
// `trackCards[i]`):
//   Signal           → card.stableTrackName
//   Signal role      → card.client.cardCategory     (Opportunity / Threat / Visibility gap / Capability gap / Monitor / Explore / Act)
//   Market zone      → card.market.marketZone (fallback card.marketZone / card.macroTheme)
//   Client exposure  → card.client.relevanceLevel   (HIGH / MEDIUM / LOW)
//   Client lens      → card.client.positioning (fallback card.clientPositioning)
//   Relevant area    → card.client.relevanceExplanation || card.client.relevanceFrontendSummary
//                       (compact, first sentence)
//   Suggested action → card.client.positioningRecommendedNextAction
//   Urgency          → card.client.urgency          (HIGH / MEDIUM / LOW)
//   Market momentum  → card.market.momentumLabel || card.market.momentum
//   Evidence         → card.market.convictionLevel  ("Broad evidence base" etc.)

import React, { useMemo } from "react";
import SortHeader from "./SortHeader";

const Badge = ({ tone = "muted", children }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

const CATEGORY_LABEL = {
    OPPORTUNITY: { label: "Opportunity", tone: "positive" },
    THREAT: { label: "Threat", tone: "negative" },
    VISIBILITY_GAP: { label: "Visibility gap", tone: "warning" },
    CAPABILITY_GAP: { label: "Capability gap", tone: "warning" },
    MONITOR: { label: "Monitor", tone: "muted" },
    EXPLORE: { label: "Explore", tone: "accent" },
    ACT: { label: "Act", tone: "ink" },
};
const URGENCY_TONE = { HIGH: "negative", MEDIUM: "warning", LOW: "muted" };
const RELEVANCE_TONE = { HIGH: "positive", MEDIUM: "warning", LOW: "muted" };

/** Map an externally-published momentum string ("Strengthening",
 *  "Holding steady", "No current weekly movement"…) or raw enum to a
 *  consistent {label, tone}. */
const momentumChip = (card) => {
    const raw = card?.market?.momentumLabel || card?.market?.momentum || "";
    const norm = String(raw).toLowerCase();
    if (!raw) return { label: "—", tone: "muted" };
    if (/no current/.test(norm)) return { label: "No current movement", tone: "muted" };
    if (/strengthen|gaining/.test(norm)) return { label: "Strengthening", tone: "positive" };
    if (/soften|fading|weaken/.test(norm)) return { label: "Softening", tone: "negative" };
    if (/holding|stable|steady/.test(norm)) return { label: "Stable", tone: "muted" };
    if (/broaden/.test(norm)) return { label: "Broadening", tone: "accent" };
    if (/mixed/.test(norm)) return { label: "Mixed momentum", tone: "muted" };
    if (/^new/.test(norm)) return { label: "New evidence", tone: "accent" };
    return { label: raw, tone: "muted" };
};

const categoryChip = (card) => {
    const raw = (card?.client?.cardCategory || "").toUpperCase();
    return CATEGORY_LABEL[raw] || { label: raw ? raw.replace(/_/g, " ").toLowerCase() : "—", tone: "muted" };
};

const cleanText = (s, maxLen = 140) => {
    if (typeof s !== "string") return null;
    const t = s.trim();
    if (!t) return null;
    // First sentence (or up to maxLen) — keeps the cell compact.
    const m = t.match(/^[^.!?]{8,}?[.!?]/);
    const head = m ? m[0] : t;
    return head.length > maxLen ? head.slice(0, maxLen - 1).trimEnd() + "…" : head;
};

const clientExposure = (card) => {
    const lvl = (card?.client?.relevanceLevel || "").toUpperCase();
    if (!lvl) return null;
    return { label: lvl.toLowerCase(), tone: RELEVANCE_TONE[lvl] || "muted" };
};

const humanisePositionEnum = (v) => {
    if (typeof v !== "string" || !v.trim()) return null;
    const map = {
        EMERGING_FIT: "Emerging fit",
        STRONG_FIT: "Strong fit",
        WEAK_FIT: "Weak fit",
        GAP: "Gap",
        LEADER: "Leader",
        LAGGARD: "Laggard",
        NOT_RELEVANT: "Not relevant",
        UNCLEAR: "Unclear",
    };
    return map[v.toUpperCase()] || v.replace(/_/g, " ").toLowerCase();
};

/** Compact lens label drawn from the structured positioning object
 *  (`client.positioning.Position`) or the legacy flattened string. */
const clientLens = (card) => {
    const p = card?.client?.positioning;
    if (p && typeof p === "object" && p.Position) return humanisePositionEnum(p.Position);
    if (typeof p === "string" && p.trim()) return humanisePositionEnum(p) || p.trim();
    if (typeof card?.clientPositioning === "string") return humanisePositionEnum(card.clientPositioning) || card.clientPositioning;
    return null;
};
/** First non-empty string from a list of candidates. Skips objects /
 *  arrays / empty strings — important because `client.relevanceExplanation`
 *  is sometimes a structured driver-breakdown object rather than the
 *  expected text. */
const firstString = (...candidates) => {
    for (const c of candidates) {
        if (typeof c === "string" && c.trim()) return c.trim();
    }
    return null;
};

const relevantArea = (card) => cleanText(firstString(
    card?.client?.relevanceFrontendSummary,
    card?.clientRelevanceRationale,
    card?.client?.relevanceRationale,
    card?.client?.positioningRationale,
    typeof card?.client?.relevanceExplanation === "string" ? card.client.relevanceExplanation : null,
), 160);
const suggestedAction = (card) => cleanText(firstString(
    card?.client?.positioningRecommendedNextAction,
    card?.client?.positioning && card.client.positioning.RecommendedNextAction,
), 180);
const marketZone = (card) => card?.market?.marketZone || card?.marketZone || card?.macroTheme || null;
const evidence = (card) => card?.market?.convictionLevel || null;
const urgency = (card) => (card?.client?.urgency || "").toUpperCase() || null;

const valueFor = (key, card) => {
    switch (key) {
        case "name": return card.stableTrackName || "";
        case "category": return categoryChip(card).label;
        case "zone": return marketZone(card) || "";
        case "exposure": return clientExposure(card)?.label || "";
        case "lens": return clientLens(card) || "";
        case "area": return relevantArea(card) || "";
        case "action": return suggestedAction(card) || "";
        case "urgency": return urgency(card) || "";
        case "momentum": return momentumChip(card).label;
        case "evidence": return evidence(card) || "";
        case "relevanceScore": return typeof card?.client?.relevanceScore === "number" ? card.client.relevanceScore : null;
        default: return null;
    }
};

const makeCompare = (sortKey, sortDir) => (a, b) => {
    const av = a.__sortValue;
    const bv = b.__sortValue;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    let cmp;
    if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
};

const Row = ({ card, selected, onSelect }) => {
    const cat = categoryChip(card);
    const mom = momentumChip(card);
    const expo = clientExposure(card);
    const lens = clientLens(card);
    const area = relevantArea(card);
    const action = suggestedAction(card);
    const zone = marketZone(card);
    const ev = evidence(card);
    const urg = urgency(card);

    return (
        <tr
            data-testid={`workbench-client-row-${card.trackId || card.canonicalTrackId}`}
            onClick={() => onSelect(card)}
            className={`cursor-pointer transition-colors ${selected ? "bg-[hsl(var(--accent-soft))]" : "hover:bg-[hsl(var(--surface-sunken))]"}`}
        >
            <td className="align-top py-2.5 pl-3 pr-3 min-w-[240px] max-w-[340px]">
                <div className="text-sm font-medium text-[hsl(var(--ink))] leading-snug">{card.stableTrackName}</div>
                <div className="text-[11px] text-[hsl(var(--mute))] mt-0.5 leading-snug">
                    <span>{cat.label}</span>
                    {zone && <><span> · </span><span>{zone}</span></>}
                    {urg && <><span> · </span><span>urgency {urg.toLowerCase()}</span></>}
                </div>
            </td>
            <td className="align-top py-2.5 px-3 whitespace-nowrap"><Badge tone={cat.tone}>{cat.label}</Badge></td>
            <td className="align-top py-2.5 px-3 text-[12px] text-[hsl(var(--ink-soft))] leading-snug max-w-[180px]">{zone || <span className="text-[hsl(var(--mute))]">—</span>}</td>
            <td className="align-top py-2.5 px-3 whitespace-nowrap">{expo ? <Badge tone={expo.tone}>{expo.label}</Badge> : <span className="text-[hsl(var(--mute))]">—</span>}</td>
            <td className="align-top py-2.5 px-3 text-[12px] text-[hsl(var(--ink-soft))] leading-snug max-w-[160px]">{lens || <span className="text-[hsl(var(--mute))]">—</span>}</td>
            <td className="align-top py-2.5 px-3 text-[12px] text-[hsl(var(--ink-soft))] leading-snug max-w-[240px]">{area || <span className="text-[hsl(var(--mute))]">—</span>}</td>
            <td className="align-top py-2.5 px-3 text-[12px] text-[hsl(var(--ink-soft))] leading-snug max-w-[260px]">{action || <span className="text-[hsl(var(--mute))]">—</span>}</td>
            <td className="align-top py-2.5 px-3 whitespace-nowrap">{urg ? <Badge tone={URGENCY_TONE[urg] || "muted"}>{urg.toLowerCase()}</Badge> : <span className="text-[hsl(var(--mute))]">—</span>}</td>
            <td className="align-top py-2.5 px-3 whitespace-nowrap"><Badge tone={mom.tone}>{mom.label}</Badge></td>
            <td className="align-top py-2.5 pr-3 pl-3 text-[12px] text-[hsl(var(--ink-soft))] max-w-[160px]">{ev || <span className="text-[hsl(var(--mute))]">—</span>}</td>
        </tr>
    );
};

const Header = ({ sortBy, sortDir, onSort }) => (
    <thead>
        <tr className="border-b border-[hsl(var(--line))]">
            <SortHeader label="Signal"          sortKey="name"      currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="pl-3 pr-3" />
            <SortHeader label="Signal role"     sortKey="category"  currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Market zone"     sortKey="zone"      currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Client exposure" sortKey="exposure"  currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="desc" className="px-3" />
            <SortHeader label="Client lens"     sortKey="lens"      currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Relevant area"   sortKey="area"      currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Suggested action" sortKey="action"   currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Urgency"         sortKey="urgency"   currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="desc" className="px-3" />
            <SortHeader label="Market momentum" sortKey="momentum"  currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Evidence"        sortKey="evidence"  currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="pl-3 pr-3" />
        </tr>
    </thead>
);

const ClientWorkbenchTable = ({ trackCards, selectedCardKey, onSelect, sortBy = "relevanceScore", sortDir = "desc", onSort }) => {
    const enriched = useMemo(() => (Array.isArray(trackCards) ? trackCards : []).map((c) => ({ card: c, __sortValue: valueFor(sortBy, c) })), [trackCards, sortBy]);
    const sorted = useMemo(() => enriched.slice().sort(makeCompare(sortBy, sortDir)), [enriched, sortBy, sortDir]);

    if (!sorted.length) return <div className="card-flat p-6 text-sm text-[hsl(var(--mute))]">No client signal cards to display.</div>;

    return (
        <div className="card-flat overflow-x-auto" data-testid="workbench-client-table">
            <table className="w-full text-sm">
                <Header sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                <tbody>
                    {sorted.map(({ card }) => {
                        const key = card.canonicalTrackId || card.trackId || card.stableTrackName;
                        return <Row key={key} card={card} selected={selectedCardKey === key} onSelect={onSelect} />;
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ClientWorkbenchTable;
